// The top line of a table or board (DESIGN.md «Верхняя строка», R-U3, um/2026-10-30/03 п.3, um/2026-10-30/04 п.3): the open view's menu,
// then filter, sort and the search; the count, the field-settings gear, «Ещё» (export, import, a letter) and «+ запись».

import { peopleFields, type Id, type SortKey, type ViewDef } from '@confdeck/core-shared';
import { useMeta } from '../sync/react.tsx';
import { useScopeWords } from '../shell/scope.tsx';
import { FIELD_ICONS } from '../ui/icons.tsx';
import { Button, Count, Menu, MenuContent, MenuItem, MenuSeparator, MenuTrigger, Popover, PopoverContent, PopoverTrigger } from '../ui/primitives.tsx';
import { TopBar } from '../ui/TopBar.tsx';
import { FilterBuilder, fromFlat, toFlat } from './FilterBuilder.tsx';
import { plural } from './DataTable.tsx';
import { ViewMenu, type ViewActions } from './ViewMenu.tsx';
import type { DrillChip } from './view-state.ts';
import s from './toolbar.module.css';

interface Props {
  typeId: Id;
  views: ViewDef[];
  view: ViewDef;
  onView: (id: Id) => void;
  update: (patch: Partial<Pick<ViewDef, 'filter' | 'sort' | 'groupBy' | 'columns' | 'layout' | 'summary' | 'gantt' | 'calendar'>>) => void;
  dirty: boolean;
  canSave: boolean;
  onSave: () => void;
  onReset: () => void;
  /** The menu of the open view (R-U2): see ViewMenu. */
  actions: ViewActions;
  search: string;
  onSearch: (q: string) => void;
  onSettings: () => void;
  /** Absent for types whose records only commands create (payments, letters, replacement requests). */
  onCreate?: () => void;
  /** Absent when the person may not import (the same rule as the server's). */
  onImport?: () => void;
  onExport: () => void;
  /** «Письмо выбранным» (J6): shown for a section with an email field to those with the right; the label says to whom. */
  onMail?: { label: string; run: () => void };
  shown: number;
  /** A table opened from a number of the summary: its conditions as chips and the way back (owner 29.10, п.5). */
  drill?: { chips: DrillChip[]; onBack: () => void };
}

export function Toolbar(p: Props) {
  const meta = useMeta();
  const t = meta.type(p.typeId);
  // A section in the words of the chosen event (R-E9): «Доступы» of a product without a date.
  const many = useScopeWords().types.get(p.typeId)?.nameMany;
  const type = t && many ? { ...t, nameMany: many } : t;
  const filterCount = toFlat(p.view.filter).items.length;

  return (
    <>
    <TopBar label="Строка фильтров" heading={p.view.name === type?.nameMany ? p.view.name : `${type?.nameMany ?? ''} · ${p.view.name}`}
      start={<ViewMenu typeId={p.typeId} views={p.views} view={p.view} onView={p.onView} update={p.update} actions={p.actions} />}
      middle={<>
        <Popover>
          <PopoverTrigger asChild>
            <Button size="sm" variant="ghost" icon="filter" pressed={filterCount > 0} aria-label="Фильтр"><span className={s.filterText}>Фильтр</span>{filterCount > 0 && <Count value={filterCount} className={s.badge} />}</Button>
          </PopoverTrigger>
          <PopoverContent className={s.filterPop}>
            <FilterBuilder typeId={p.typeId} value={toFlat(p.view.filter)} onChange={(ff) => p.update({ filter: fromFlat(ff) })} />
          </PopoverContent>
        </Popover>
        <SortMenu typeId={p.typeId} sort={p.view.sort} onChange={(sort) => p.update({ sort })} />
      </>}
      search={{ value: p.search, onChange: p.onSearch, placeholder: `Найти в «${type?.nameMany ?? ''}»`, slash: true }}
      count={<Count value={p.shown.toLocaleString('ru-RU')} />}
      actions={[
        ...(p.dirty && !p.drill ? [{ key: 'reset', label: 'Сбросить', onClick: p.onReset }, ...(p.canSave ? [{ key: 'save', label: 'Сохранить вид', onClick: p.onSave }] : [])] : []),
        { key: 'fields', label: 'Поля и таксономии', icon: 'gear', iconOnly: true, onClick: p.onSettings },
        ...(p.onCreate ? [{ key: 'create', label: type?.name ?? 'Запись', icon: 'plus', primary: true, onClick: p.onCreate }] : []),
      ]}
      more={<>
        <MenuItem icon="external" onSelect={p.onExport}>Выгрузить в CSV ({p.shown.toLocaleString('ru-RU')})</MenuItem>
        {p.onMail && <MenuItem icon="mail" onSelect={p.onMail.run}>{p.onMail.label}</MenuItem>}
        {p.onImport && <MenuItem icon="plus" onSelect={p.onImport}>Загрузить из CSV</MenuItem>}
      </>} />
    {p.drill && (
      <div className={s.drill} role="region" aria-label="Записи из сводки">
        <Button size="sm" icon="back" onClick={p.drill.onBack}>К сводке</Button>
        {p.drill.chips.map((c) => <span key={c.label} className={s.drillChip}><span className={s.drillLabel}>{c.label}:</span> {c.value}</span>)}
        <span className={s.drillCount}>{p.shown.toLocaleString('ru-RU')} {plural(p.shown, 'запись', 'записи', 'записей')}</span>
      </div>
    )}
    </>
  );
}

function SortMenu({ typeId, sort, onChange }: { typeId: Id; sort: SortKey[]; onChange: (s: SortKey[]) => void }) {
  const meta = useMeta();
  const fields = peopleFields(meta.fields(typeId)).filter((f) => f.fieldType !== 'relation' && f.fieldType !== 'longtext');
  const first = sort[0];
  const label = first ? (meta.field(typeId, first.field)?.name ?? ({ createdAt: 'Создано', updatedAt: 'Изменено' } as Record<string, string>)[first.field] ?? first.field) : null;
  return (
    <Menu>
      <MenuTrigger asChild><Button size="sm" icon="sort" pressed={!!first} aria-label="Сортировка"><span className={s.sortText}>{label ? `${label} ${first!.dir === 'asc' ? '↑' : '↓'}` : 'Сортировка'}</span></Button></MenuTrigger>
      <MenuContent>
        {fields.map((f) => (
          <MenuItem key={f.id} icon={FIELD_ICONS[f.fieldType]} onSelect={() => onChange([{ field: f.key, dir: first?.field === f.key && first.dir === 'asc' ? 'desc' : 'asc' }])}>
            {f.name}{first?.field === f.key ? (first.dir === 'asc' ? ' ↑' : ' ↓') : ''}
          </MenuItem>
        ))}
        <MenuItem icon="date" onSelect={() => onChange([{ field: 'createdAt', dir: 'desc' }])}>Сначала новые</MenuItem>
        <MenuItem icon="date" onSelect={() => onChange([{ field: 'updatedAt', dir: 'desc' }])}>Недавно изменённые</MenuItem>
        {first && <><MenuSeparator /><MenuItem icon="close" onSelect={() => onChange([])}>Без сортировки</MenuItem></>}
      </MenuContent>
    </Menu>
  );
}
